/**
 * Magasin `employees` : les employes du magasin du poste, leurs droits et,
 * pour ceux qui se sont deja connectes EN LIGNE sur ce poste, le
 * verificateur PBKDF2 de leur NIP (app/pinVerifier.js).
 *
 * Forme d'un enregistrement (cle : employee_id) :
 *   { employee_id, display_name, has_pin, rights: { can_discount,
 *     max_discount_pct, can_override_price, can_close_shift, is_manager },
 *     pin_verifier: Uint8Array(32) | null, pin_kdf: { alg, iterations } | null,
 *     last_online_login_at, revoked, revoked_at, revoked_reason,
 *     roster_synced_at }
 *
 * Jamais de NIP en clair, jamais de hash bcrypt du serveur.
 */
import { requestToPromise, transactionDone } from './idb';
import { META_KEYS, STORES } from './schema';
import { PIN_KDF, PIN_SALT_BYTES, constantTimeEqual, isBytes } from '../app/pinVerifier';

export const NO_RIGHTS = Object.freeze({
    can_discount: false,
    max_discount_pct: null,
    can_override_price: false,
    can_close_shift: false,
    is_manager: false,
});

/** Droits recus du serveur, ramenes a des booleens stricts (absent = non). */
export function normalizeRights(rights) {
    const r = rights && typeof rights === 'object' ? rights : {};
    const pct = Number(r.max_discount_pct);
    return {
        can_discount: r.can_discount === true,
        max_discount_pct: r.max_discount_pct === null || r.max_discount_pct === undefined || !Number.isFinite(pct)
            ? null
            : pct,
        can_override_price: r.can_override_price === true,
        can_close_shift: r.can_close_shift === true,
        is_manager: r.is_manager === true,
    };
}

/**
 * Sel du poste : 16 octets aleatoires crees une seule fois, dans la meme
 * transaction que leur lecture (deux appels simultanes ne creent pas deux sels).
 * @returns {Promise<Uint8Array>}
 */
export async function getOrCreatePinSalt(db, cryptoImpl = globalThis.crypto) {
    const tx = db.transaction(STORES.META, 'readwrite');
    const store = tx.objectStore(STORES.META);
    const done = transactionDone(tx);
    const record = await requestToPromise(store.get(META_KEYS.PIN_SALT));
    let salt = record && isBytes(record.value) && record.value.length === PIN_SALT_BYTES
        ? record.value
        : null;
    if (!salt) {
        if (!cryptoImpl || typeof cryptoImpl.getRandomValues !== 'function') {
            tx.abort();
            await done.catch(() => {});
            throw new Error('Generateur aleatoire indisponible');
        }
        salt = cryptoImpl.getRandomValues(new Uint8Array(PIN_SALT_BYTES));
        store.put({ key: META_KEYS.PIN_SALT, value: salt });
    }
    await done;
    return salt;
}

/** Tous les employes connus du poste. */
export async function listEmployees(db) {
    const tx = db.transaction(STORES.EMPLOYEES, 'readonly');
    return requestToPromise(tx.objectStore(STORES.EMPLOYEES).getAll());
}

/**
 * Connexion EN LIGNE reussie : garde (ou remplace) le verificateur de cet
 * employe, met a jour son nom et ses droits. Le serveur vient de dire que ce
 * NIP est le sien : un AUTRE employe qui aurait le meme verificateur (son NIP
 * a change dans l'ERP depuis) le perd, dans la meme transaction.
 */
export async function saveOnlineLogin(db, { employee, verifier, now = new Date() }) {
    const tx = db.transaction(STORES.EMPLOYEES, 'readwrite');
    const store = tx.objectStore(STORES.EMPLOYEES);
    const done = transactionDone(tx);
    const all = await requestToPromise(store.getAll());
    const at = now.toISOString();
    for (const other of all) {
        if (other.employee_id !== employee.employee_id && constantTimeEqual(other.pin_verifier, verifier)) {
            store.put({ ...other, pin_verifier: null, pin_kdf: null });
        }
    }
    const current = all.find((e) => e.employee_id === employee.employee_id) || {};
    store.put({
        ...current,
        employee_id: employee.employee_id,
        display_name: employee.display_name,
        has_pin: true,
        rights: normalizeRights(employee.rights),
        pin_verifier: verifier,
        pin_kdf: { alg: PIN_KDF.alg, iterations: PIN_KDF.iterations },
        last_online_login_at: at,
        revoked: false,
        revoked_at: null,
        revoked_reason: null,
    });
    await done;
}

/**
 * Le serveur a refuse ce NIP (401) : un verificateur local identique est
 * perime (NIP change dans l'ERP). On l'efface pour qu'il ne serve plus hors ligne.
 * @returns {Promise<number>} nombre de verificateurs effaces
 */
export async function forgetVerifier(db, verifier) {
    const tx = db.transaction(STORES.EMPLOYEES, 'readwrite');
    const store = tx.objectStore(STORES.EMPLOYEES);
    const done = transactionDone(tx);
    const all = await requestToPromise(store.getAll());
    let count = 0;
    for (const employee of all) {
        if (constantTimeEqual(employee.pin_verifier, verifier)) {
            store.put({ ...employee, pin_verifier: null, pin_kdf: null });
            count += 1;
        }
    }
    await done;
    return count;
}
